import { useSearchParams } from 'react-router';
import slugify from 'slugify';

import { interpolate } from './string';
import { CompanyProfile } from '../../company/entities';
import { ShipmentViewMode } from '../../tour/utils';
import { getEmbedConfig } from '../data';

export const shipmentViewUrl = (companyId: string | null, tourId: string | null, mode: ShipmentViewMode) => {
  const config = getEmbedConfig();

  if (config === null || companyId === null || tourId === null) {
    return;
  }

  let urlTemplate;
  if (mode === 'air') {
    urlTemplate = config.air_shipment_view_url;
  } else if (mode === 'ocean') {
    urlTemplate = config.ocean_shipment_view_url;
  } else if (mode === 'inland') {
    urlTemplate = config.inland_shipment_view_url;
  } else {
    urlTemplate = config.shipment_view_url;
  }

  return interpolate(urlTemplate, {
    company_id: companyId,
    tour_id: tourId,
  });
};
export const shipmentsViewUrl = (companyId: string | null) => {
  const config = getEmbedConfig();

  if (config === null || companyId === null) {
    return;
  }

  return interpolate(config.shipments_view_url, { company_id: companyId });
};

export const placeViewUrl = (tourCreatedBy: CompanyProfile | null, placeId: string | null) => {
  const config = getEmbedConfig();

  if (config === null || tourCreatedBy === null || placeId === null) {
    return;
  }

  return interpolate(config.place_view_url, { company_id: tourCreatedBy.company_id, place_id: placeId });
};

export const classicMapViewByCoordinatesUrl = ({ lat, lng }: { lat: number | null; lng: number | null }) => {
  const config = getEmbedConfig();

  if (lat === null || lng === null || config === null) {
    return;
  }

  return interpolate(config.google_maps_lat_lng_url, {
    lat,
    lng,
  });
};

export const classicMapViewByAddressUrl = ({ address }: { address: string }) => {
  const config = getEmbedConfig();

  if (config === null) {
    return;
  }

  return interpolate(config.google_maps_address_url, {
    address: encodeURIComponent(address),
  });
};

export const openSatelliteViewUrl = ({ lat, lng }: { lat: number | null; lng: number | null }) => {
  const config = getEmbedConfig();

  if (lat === null || lng === null || config === null) {
    return;
  }
  return window.open(interpolate(config.satelliteview_lat_lng_url, { lat, lng }), '_blank');
};

export const tourStopEtaHistoryUrl = ({ tourId, stopId }: { tourId: number; stopId: number }) => {
  const config = getEmbedConfig();

  if (config === null) {
    return;
  }

  const stopSpecification = {
    'ds0.tour_id': tourId.toString(),
    'ds0.stop_id': stopId.toString(),
  };

  return interpolate(config.data_studio_stop_eta_dashboard_url, {
    params: encodeURIComponent(JSON.stringify(stopSpecification)),
  });
};

export type UrlSort<Key extends string> = { key: Key; direction: 'ASC' | 'DESC' };
export const useUrlSort = <Key extends string>(defaultSort: UrlSort<Key>) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const sort = {
    key: (searchParams.get('sort') ?? defaultSort.key) as Key,
    direction: (searchParams.get('sort_order')?.toUpperCase() ?? defaultSort.direction) as 'ASC' | 'DESC',
  };

  const changeSort = (newSort: UrlSort<Key>) => {
    setSearchParams((prev) => {
      prev.set('sort', newSort.key);
      prev.set('sort_order', newSort.direction.toLowerCase());
      return prev;
    });
  };

  return {
    sort,
    changeSort,
  };
};

// Placeholder slug used in company URLs when the company name is missing or
// produces an empty slug (e.g. names containing only non-Latin characters such
// as Chinese).
export const COMPANY_SLUG_PLACEHOLDER = '-';

export const customSlugify = (value: string | null | undefined): string => {
  const slug = value ? slugify(value, { strict: true }) : '';
  return slug !== '' ? slug : COMPANY_SLUG_PLACEHOLDER;
};
